import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Box,
  Typography,
  Divider,
  Grid,
} from '@mui/material';

const ChartAdvancedConfig = ({ open, onClose, onSave, chartConfig, chartType, currentLabel }) => {
  const [config, setConfig] = useState({
    title: '',
    xAxisTitle: '',
    yAxisTitle: '',
    legendPosition: 'top',
    legendDisplay: true,
    gridLines: true,
    xAxisMin: '',
    xAxisMax: '',
    yAxisMin: '',
    yAxisMax: '',
    pointRadius: '',
    borderWidth: '',
  });

  useEffect(() => {
    if (open) {
      // Precargar valores: primero de chartConfig, luego de currentLabel para el título
      setConfig({
        title: chartConfig?.title !== undefined ? chartConfig.title : (currentLabel || ''),
        xAxisTitle: chartConfig?.xAxisTitle || '',
        yAxisTitle: chartConfig?.yAxisTitle || '',
        legendPosition: chartConfig?.legendPosition || 'top',
        legendDisplay: chartConfig?.legendDisplay !== false,
        gridLines: chartConfig?.gridLines !== false,
        xAxisMin: chartConfig?.xAxisMin || '',
        xAxisMax: chartConfig?.xAxisMax || '',
        yAxisMin: chartConfig?.yAxisMin || '',
        yAxisMax: chartConfig?.yAxisMax || '',
        pointRadius: chartConfig?.pointRadius || '',
        borderWidth: chartConfig?.borderWidth || '',
      });
    }
  }, [chartConfig, currentLabel, open]);

  const handleSave = () => {
    onSave(config);
    onClose();
  };

  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const isAxisChart = chartType === 'line' || chartType === 'bar' || chartType === 'scatter';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Configuración Avanzada del Gráfico</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
          {/* Título del gráfico */}
          <TextField
            label="Título del gráfico"
            value={config.title}
            onChange={(e) => handleChange('title', e.target.value)}
            fullWidth
            size="small"
            placeholder="Opcional"
          />

          <Divider />

          {/* Configuración de leyenda */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Leyenda
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.legendDisplay}
                      onChange={(e) => handleChange('legendDisplay', e.target.checked)}
                    />
                  }
                  label="Mostrar leyenda"
                />
              </Grid>
              {config.legendDisplay && (
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Posición de la leyenda</InputLabel>
                    <Select
                      value={config.legendPosition}
                      label="Posición de la leyenda"
                      onChange={(e) => handleChange('legendPosition', e.target.value)}
                    >
                      <MenuItem value="top">Arriba</MenuItem>
                      <MenuItem value="bottom">Abajo</MenuItem>
                      <MenuItem value="left">Izquierda</MenuItem>
                      <MenuItem value="right">Derecha</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </Box>

          {isAxisChart && (
            <>
              <Divider />

              {/* Configuración de ejes */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Ejes
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Título del eje X"
                      value={config.xAxisTitle}
                      onChange={(e) => handleChange('xAxisTitle', e.target.value)}
                      fullWidth
                      size="small"
                      placeholder="Opcional"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Título del eje Y"
                      value={config.yAxisTitle}
                      onChange={(e) => handleChange('yAxisTitle', e.target.value)}
                      fullWidth
                      size="small"
                      placeholder="Opcional"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={config.gridLines}
                          onChange={(e) => handleChange('gridLines', e.target.checked)}
                        />
                      }
                      label="Mostrar líneas de cuadrícula"
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Límites de escalas */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Límites de Escalas (Opcional)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Mínimo eje X"
                      value={config.xAxisMin}
                      onChange={(e) => handleChange('xAxisMin', e.target.value)}
                      fullWidth
                      size="small"
                      type="number"
                      placeholder="Auto"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Máximo eje X"
                      value={config.xAxisMax}
                      onChange={(e) => handleChange('xAxisMax', e.target.value)}
                      fullWidth
                      size="small"
                      type="number"
                      placeholder="Auto"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Mínimo eje Y"
                      value={config.yAxisMin}
                      onChange={(e) => handleChange('yAxisMin', e.target.value)}
                      fullWidth
                      size="small"
                      type="number"
                      placeholder="Auto"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Máximo eje Y"
                      value={config.yAxisMax}
                      onChange={(e) => handleChange('yAxisMax', e.target.value)}
                      fullWidth
                      size="small"
                      type="number"
                      placeholder="Auto"
                    />
                  </Grid>
                </Grid>
              </Box>
            </>
          )}

          {(chartType === 'line' || chartType === 'scatter') && (
            <>
              <Divider />

              {/* Configuración de puntos */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Puntos
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Radio de puntos"
                      value={config.pointRadius}
                      onChange={(e) => handleChange('pointRadius', e.target.value)}
                      fullWidth
                      size="small"
                      type="number"
                      placeholder="Auto (3)"
                      inputProps={{ min: 0, max: 20, step: 0.5 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Ancho del borde"
                      value={config.borderWidth}
                      onChange={(e) => handleChange('borderWidth', e.target.value)}
                      fullWidth
                      size="small"
                      type="number"
                      placeholder="Auto (2)"
                      inputProps={{ min: 0, max: 10, step: 0.5 }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={handleSave} variant="contained">
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChartAdvancedConfig;
